import React from 'react';
import AuthStore from 'stores/AuthStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import UserStore from 'stores/UserStore';
import UserActions from 'actions/UserActions';
import {List, Record} from 'immutable';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import DashV2MainWidgetLoader from 'components/dashboard/DashV2MainWidgetLoader';
import DirectReportsSource from 'sources/coaching/DirectReportsSource';
import CoachingPlansWidget from 'components/dashboard/CoachingPlansWidget';
import {shouldUpdate} from 'components/StatsPage2.utils';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';
import {refreshCoachingPlansAndVelocities} from 'components/coaching/landing-pages/RefreshCoachingPlansAndVelocities';

class State extends Record({
  ready: false,
  directReportsUserIds: List(),
}) {}

/**
 * This container exists to ensure a standard downstream manager user id.
 *
 * Did we come from a route? Then we've come from exec view. Ensure that we use THAT manager id
 * Otherwise, assume we're at the landing page for a manager.  Use the authuser id
 */
@connectToStores
export default class ManagerCoachingPageContainer extends React.Component {
  constructor(props) {
    super(props);
    this.refreshCoachingData = this.refreshCoachingData.bind(this);
    this.refreshUpcomingReviewData = this.refreshUpcomingReviewData.bind(this);
    this.state = new State();
  }

  static getStores() {
    return [AuthStore, UserStore];
  }

  static getPropsFromStores() {
    return {
      users: UserStore.getState().users,
      authUser: AuthStore.getState().user,
    };
  }

  managerId(props) {
    const propsToCheck = props || this.props;
    const {usePropsManagerId, forCoach, managerId, coachId, routeParams, authUser} = propsToCheck;
    return usePropsManagerId ? (forCoach ? coachId : managerId) : routeParams.userId || authUser.id;
  }

  refreshCoachingData() {
    // Find plans where they're the coach or manager, then gather everything else
    const managerId = this.managerId();
    refreshCoachingPlansAndVelocities(managerId).then(this.refreshUpcomingReviewData);
  }

  refreshUpcomingReviewData() {
    const managerId = this.managerId();

    // if loading for the coach dash, get coaching plans where they're
    // the recipient and the coach.
    const upcomingReviewsRole = this.props.coachId
      ? UPCOMING_REVIEW_ROLES.ALL
      : UPCOMING_REVIEW_ROLES.COACH;
    UpcomingReviewsActions.upcomingReviews(managerId, upcomingReviewsRole);
  }

  /** Fetch data for the manager. This data will not change with plans, only when the user navigates.
   *
   * why ready?
   * we cannot rely on alt loading states because they do not coordinate firing when they are loading
   * with render cycles. what happens is that we render once NOT explicitly loading, then start loading the next cycle,
   * which is especially annoying because we'll display what was in state before we start loading
   */
  async refreshData() {
    if (this.state.loading) {
      return;
    }

    this.setState(
      {
        ready: false,
        loading: true,
      },
      async () => {
        const managerId = this.managerId();
        UserActions.fetchAll.defer({ids: List([managerId])});
        // The page is displayed differently, depending on whether or not the user
        // has direct reports. This also affects the users available in the
        // dropdown in the wizard.
        const teams = await DirectReportsSource.directReports(managerId);
        const directReportsUserIds = teams
          .flatMap(team => team.users.map(user => user.id))
          .filter(id => id !== managerId);

        await this.refreshCoachingData();

        this.setState({
          ready: true,
          loading: false,
          directReportsUserIds,
        });
      }
    );
  }

  /**
   * Ensure that data is fetched when we navigate from one manager's page to another. If the previous managerId and new
   * managerId differ, fetch new data. Needed because this component isn't mounted when this happens.
   */
  async componentDidUpdate(prevProps) {
    if (
      this.state.ready &&
      (this.managerId(prevProps) !== this.managerId() ||
        shouldUpdate(prevProps, this.props, false, false, true))
    ) {
      await this.refreshData();
    }
  }

  async componentDidMount() {
    await this.refreshData();
  }

  componentWillUnmount() {
    this.setState({ready: false, directReportsUserIds: List()});
  }

  render() {
    if (!this.state.ready) {
      return this.props.hasRenderedDashv2Gif ? <DashV2MainWidgetLoader /> : null;
    }
    const {adminId, manager, managerId, coach, coachId} = this.props;
    const isManagerLoggedIn = this.managerId() === this.props.authUser.id;
    const {showMoreLink, plansLimit} = this.props;

    return (
      <CoachingPlansWidget
        adminId={adminId}
        managerId={managerId}
        manager={coach ? coach : manager}
        coachId={coachId}
        selectedTeam={this.props.selectedTeam}
        startDate={this.props.startDate}
        endDate={this.props.endDate}
        authUser={this.props.authUser}
        isManagerLoggedIn={isManagerLoggedIn}
        directReportsUserIds={this.state.directReportsUserIds}
        refreshCoachingData={this.refreshCoachingData}
        fromPath={this.props.fromPath}
        suppressToggle={this.props.suppressToggle}
        plansLimit={plansLimit}
        showMoreLink={showMoreLink}
        isSummary={this.props.isSummary}
        location={this.props.location}
      />
    );
  }
}
